import Transaction from 'models/Transaction';

/**
 * The right-hand block of the ledger: the spreadsheet's E3:L35, every transaction in the period.
 *
 * Ordered oldest first, which is the opposite of every other list in this app. That is the sheet's
 * order and it is the order the entry loop needs: she types the month down the page, so the row
 * she is about to add belongs at the bottom, next to the blank one, rather than scrolling the
 * whole month down by one every time a row lands.
 *
 * The transactions are clones -- `TransactionFinderFilter` hands them over that way -- so a cell
 * edit is a change to a copy until it is submitted, and an abandoned edit costs nothing.
 */

/** Oldest first, undated rows last, and same-day rows in the order they were entered. */
export function ledgerSort(a, b) {
	if(!a.transaction_date && !b.transaction_date) {
		return Transaction.idCompare(b, a);
	}
	if(!a.transaction_date) {
		return 1;
	}
	if(!b.transaction_date) {
		return -1;
	}
	if(a.transaction_date.isSame(b.transaction_date, 'day')) {
		return Transaction.idCompare(b, a);
	}
	return a.transaction_date.isBefore(b.transaction_date) ? -1 : 1;
}

/**
 * @param transactionFinder {Object} the dashboard's finder
 * @param selectedDates {Object} the selected months, keyed by year-month
 * @returns {{transactions: Array, total: number}} total is cents, and is every row's amount summed
 */
export default function recordingRows(transactionFinder, selectedDates) {
	if(!transactionFinder || !selectedDates) {
		return { transactions: [], total: 0 };
	}
	const transactions = transactionFinder
		.getTransactionFinderFilter()
		.setWithVisibleUserTransactions(true)
		.getTransactionsByDates(selectedDates)
		.sort(ledgerSort);

	// Everything in the period, income included, rather than expenses alone. A ledger that
	// silently drops rows is worse than one whose total is a net figure: a transaction you cannot
	// see is one you will enter twice.
	const total = transactions.reduce((sum, transaction) => sum + (transaction.amount || 0), 0);

	return { transactions, total };
}
